/**
 * 安聆 SafeListen - 配对码生成模块（Supabase 存储 + Realtime，无轮询）
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, message } from 'antd';
import { buildPairingCode, generateRandomHash, getPairingCodeDisplay } from '../utils/hashUtils';
import { supabase, TABLE_ACTIVE_CODES } from '../utils/supabase';
import './HashGenerator.css';

const MAX_RETRIES = 10;

export default function HashGenerator({ isMonitor, onPairingCodeChange, currentCode }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [pairingCode, setPairingCode] = useState(currentCode || '');
  const [releasing, setReleasing] = useState(false);

  useEffect(() => {
    setPairingCode((currentCode || '').trim());
  }, [currentCode]);

  const releaseHash = useCallback(async () => {
    if (!pairingCode || !supabase) return;
    const hash = pairingCode.split('-')[1]?.trim().toUpperCase();
    if (!hash || hash.length !== 6) return;
    setReleasing(true);
    try {
      const { error } = await supabase.from(TABLE_ACTIVE_CODES).delete().eq('hash', hash);
      if (error) throw error;
      message.success(t('guard.disconnectSuccess'));
      setPairingCode('');
      onPairingCodeChange?.('');
    } catch (e) {
      message.error(t('hash.releaseFailed'));
    } finally {
      setReleasing(false);
    }
  }, [pairingCode, onPairingCodeChange, t]);

  const generateUniqueHash = useCallback(async () => {
    if (!isMonitor || !supabase) return;
    setLoading(true);
    const oldCode = pairingCode;
    if (oldCode) {
      const oldHash = oldCode.split('-')[1]?.trim().toUpperCase();
      if (oldHash && oldHash.length === 6) {
        await supabase.from(TABLE_ACTIVE_CODES).delete().eq('hash', oldHash);
      }
    }
    for (let i = 0; i < MAX_RETRIES; i++) {
      const hash = generateRandomHash();
      const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // 1 小时后过期，有人连接后置空
      const { error } = await supabase.from(TABLE_ACTIVE_CODES).insert({ hash, expires_at: expiresAt });
      if (!error) {
        const code = buildPairingCode(hash);
        setPairingCode(code);
        onPairingCodeChange?.(code);
        message.success(t('hash.success'));
        setLoading(false);
        return;
      }
      if (error.code !== '23505') {
        message.error(t('hash.failed'));
        setLoading(false);
        return;
      }
    }
    message.error(t('hash.failed'));
    setLoading(false);
  }, [isMonitor, pairingCode, onPairingCodeChange, t]);

  if (!isMonitor) return null;

  return (
    <div className="safelisten-card hash-generator">
      <Button
        type="default"
        className="safelisten-btn-secondary"
        loading={loading}
        disabled={loading}
        onClick={generateUniqueHash}
      >
        {loading ? t('hash.generating') : t('hash.generate')}
      </Button>
      {pairingCode && (
        <div className="pairing-result">
          <span
            className="safelisten-highlight code-text pairing-code-copy"
            title={t('mode.copyCode')}
            onClick={() => {
              const toCopy = getPairingCodeDisplay(pairingCode, t);
              if (toCopy) navigator.clipboard.writeText(toCopy).then(() => message.success(t('mode.copied'))).catch(() => {});
            }}
          >
            {getPairingCodeDisplay(pairingCode, t)}
          </span>
          <Button
            type="primary"
            danger
            className="safelisten-btn-warning"
            size="small"
            loading={releasing}
            disabled={releasing}
            onClick={releaseHash}
          >
            {t('guard.disconnect')}
          </Button>
        </div>
      )}
    </div>
  );
}
